import { useState, useRef, useCallback, useEffect } from "react";

// Declare global types for window properties
declare global {
  interface Window {
    Recorder: any;
    webkitAudioContext: typeof AudioContext;
  }
}

// Configuration constants
const MIN_AUDIO_DURATION_MS = 500;
const MAX_AUDIO_DURATION_MS = 60000;

// This ref lives outside the component to ensure it's a true singleton
// for the entire app lifecycle, preventing script re-injection in StrictMode.
const scriptStatus = {
  loaded: false,
  loading: false,
};

// Define the types for the callbacks the hook will accept
type AudioRecorderCallbacks = {
  onRecordingComplete: (blob: Blob) => void;
  onRecordingStarted?: () => void;
  onRecordingStopped?: () => void;
  onError?: (error: Error) => void;
};

export function useAudioRecorder({
  onRecordingComplete,
  onRecordingStarted,
  onRecordingStopped,
  onError,
}: AudioRecorderCallbacks) {
  const [isRecording, setIsRecording] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);
  const [duration, setDuration] = useState(0);

  const recorderRef = useRef<any>(null);
  const analyserRef = useRef<any>(null);
  const recordingContextRef = useRef<AudioContext | null>(null);
  const lastRecordStartRef = useRef<number>(0);
  const durationIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const cleanup = useCallback(() => {
    // Clear any running timers
    if (durationIntervalRef.current) clearInterval(durationIntervalRef.current);

    // Properly close the audio context and recorder instance if they exist
    recorderRef.current?.close();
    recordingContextRef.current?.close();
    
    recorderRef.current = null;
    recordingContextRef.current = null;
  }, []);
  
  const initializeRecorder = useCallback(async () => {
    if (recorderRef.current || !window.Recorder) return;

    console.log("Initializing recorder...");
    setIsInitializing(true);

    try {
      // Monkeypatch is fine, the internal flag prevents it from running multiple times
      if (!window.Recorder.prototype._isPatched) {
          window.Recorder.prototype.encodeAudio = async function (audioFloat32Array: Float32Array): Promise<Blob> { 
            return new Promise((resolve, reject) => {
              const originalOndataavailable = this.ondataavailable;
              const originalOnstart = this.onstart;
              const originalOnstop = this.onstop;
              const encodedChunks: any[] = [];
              this.ondataavailable = (typedArray: any) => {
                encodedChunks.push(typedArray);
              };
              this.onstart = () => {
                if (this.encoder && this.encoder.postMessage) {
                  this.encoder.postMessage({
                    command: "encode",
                    buffers: [audioFloat32Array],
                  });
                }
              };
              this.onstop = () => {
                const opusBlob = new Blob(encodedChunks, {
                  type: "audio/ogg; codecs=opus",
                });
                this.ondataavailable = originalOndataavailable;
                this.onstart = originalOnstart;
                this.onstop = originalOnstop;
                resolve(opusBlob);
              };
              this.start().then(() => { this.stop(); }).catch(reject);
            });
          };
          window.Recorder.prototype._isPatched = true;
      }

      // Create and resume AudioContext
      const context = new (window.AudioContext || window.webkitAudioContext)({ sampleRate: 16000 });
      if (context.state === 'suspended') {
        await context.resume();
      }
      recordingContextRef.current = context;

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const sourceNode = context.createMediaStreamSource(stream);
      
      const analyserNode = context.createAnalyser();
      analyserNode.fftSize = 64;
      sourceNode.connect(analyserNode);
      analyserRef.current = analyserNode;

      const recorder = new window.Recorder({
        numberOfChannels: 1,
        encoderSampleRate: 16000,
        encoderComplexity: 6,
        originalSampleRate: context.sampleRate,
        streamPages: true,
        sourceNode: sourceNode,
      });

      let audioChunks: Uint8Array[] = [];

      recorder.ondataavailable = (typedArray: Uint8Array) => {
        audioChunks.push(typedArray);
      };

      recorder.onstart = () => {
        setIsRecording(true);
        lastRecordStartRef.current = Date.now();
        audioChunks = [];
        onRecordingStarted?.();

        // Start duration timer
        durationIntervalRef.current = setInterval(() => {
          setDuration(Date.now() - lastRecordStartRef.current);
        }, 100);
      };

      recorder.onstop = () => {
        if (durationIntervalRef.current) clearInterval(durationIntervalRef.current);
        setIsRecording(false);
        setDuration(0);
        onRecordingStopped?.();

        const recordingDuration = Date.now() - lastRecordStartRef.current;
        if (recordingDuration < MIN_AUDIO_DURATION_MS) {
          console.log(`Recording too short (${recordingDuration}ms), skipping.`);
          return;
        }

        if (audioChunks.length > 0) {
          const blob = new Blob(audioChunks, { type: "audio/ogg; codecs=opus" });
          onRecordingComplete(blob);
        } else {
          console.warn("No audio chunks recorded.");
        }
      };

      await recorder.initialize;
      recorderRef.current = recorder;
      console.log("Recorder initialized successfully");

    } catch (error: any) {
      console.error("Error initializing recorder:", error);
      onError?.(error);
      cleanup(); // Clean up resources on failure
    } finally {
      setIsInitializing(false);
    }
  }, [onRecordingComplete, onRecordingStarted, onRecordingStopped, onError, cleanup]);

  // Effect to load the script ONCE and then initialize
  useEffect(() => {
    // If script is already loaded, just initialize
    if (scriptStatus.loaded) {
      initializeRecorder();
      return;
    }

    // If script is currently loading, do nothing and wait
    if (scriptStatus.loading) {
      return;
    }

    // Start loading the script
    scriptStatus.loading = true;
    setIsInitializing(true);

    const script = document.createElement("script");
    script.src = "https://cdn.chotto.chat/js/opus-encdec/recorder.min.js";
    script.async = true;
    
    script.onload = () => {
      scriptStatus.loaded = true;
      scriptStatus.loading = false;
      initializeRecorder();
    };

    script.onerror = () => {
      scriptStatus.loading = false;
      const loadError = new Error("Failed to load recorder script.");
      onError?.(loadError);
      setIsInitializing(false);
    };

    document.body.appendChild(script);

    // The cleanup function for unmounting the component
    return () => {
        cleanup();
    };
  }, [initializeRecorder, onError, cleanup]);


  const startRecording = useCallback(async () => {
    if (isRecording || isInitializing || !recorderRef.current) return;
    try {
      // Automatically stop recording after max duration
      setTimeout(stopRecording, MAX_AUDIO_DURATION_MS);
      await recorderRef.current.start();
    } catch (error: any) {
      console.error("Error starting recording:", error);
      onError?.(error);
    }
  }, [isRecording, isInitializing, onError]);

  const stopRecording = useCallback(async () => {
    if (!isRecording || isInitializing || !recorderRef.current) return;
    try {
      await recorderRef.current.stop();
    } catch (error: any) {
      console.error("Error stopping recording:", error);
      onError?.(error);
    }
  }, [isRecording, isInitializing, onError]);

  return { isRecording, isInitializing, startRecording, stopRecording, duration, analyserRef };
}